import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, StickyNote } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Area, Card, DeleteBtn, IconBtn, PageTitle, Pill, Select, Skel, YBtn, useSaver } from "@/admin/ui";
import { adminApi, fmtDate, useAdmin, useRefresh } from "@/admin/lib";
import { errorMessage } from "@/lib/api";

const ReplyDialog = ({ g, onClose, onSaved }) => {
  const [resolution, setRes] = useState(g.resolution || "");
  const [status, setStatus] = useState(g.status);
  const [saving, run] = useSaver();
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-2xl bg-white" data-testid="grievance-dialog">
        <DialogHeader><DialogTitle className="font-heading text-navy">{g.reference}: {g.subject}</DialogTitle></DialogHeader>
        <p className="text-sm text-body"><b>{g.name}</b> · {g.email} {g.phone && `· ${g.phone}`} {g.order_id && `· Order ${g.order_id}`}</p>
        <p className="rounded-xl bg-page p-3 text-sm text-body">{g.description}</p>
        <Select value={status} onChange={(e) => setStatus(e.target.value)} options={["Open", "Acknowledged", "Resolved", "Closed"]} testId="grievance-status-select" />
        <Area rows={4} value={resolution} onChange={(e) => setRes(e.target.value)} placeholder="Reply / resolution notes" testId="grievance-reply-input" />
        <YBtn loading={saving} onClick={async () => { if (await run(() => adminApi.put(`/admin/records/grievances/${g.id}`, { status, resolution }))) { onSaved(); onClose(); } }} testId="grievance-save-btn">Save</YBtn>
      </DialogContent>
    </Dialog>
  );
};

const DeletionTable = () => {
  const { data, isLoading } = useAdmin("/admin/records/data-deletion-requests?limit=200");
  const refresh = useRefresh();
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); refresh("/admin/records/data-deletion-requests"); } catch (e) { toast.error(errorMessage(e)); } };
  if (isLoading) return <Skel />;
  const items = data?.items || [];
  return items.length ? (
    <div className="overflow-x-auto"><table className="w-full min-w-[700px] text-sm" data-testid="deletion-table">
      <thead><tr className="border-b border-line text-left text-xs uppercase tracking-wider text-muted2">{["Reference", "Name", "Contact", "Message", "Status", "Date", ""].map((h) => <th key={h} className="py-2 pr-3">{h}</th>)}</tr></thead>
      <tbody>{items.map((r) => (
        <tr key={r.id} className="border-b border-line/60 last:border-0" data-testid={`deletion-row-${r.reference}`}>
          <td className="py-3 pr-3 font-semibold text-navy">{r.reference}</td><td className="py-3 pr-3">{r.name}</td><td className="py-3 pr-3">{r.contact}</td>
          <td className="max-w-[240px] py-3 pr-3 text-xs text-body">{r.message || "-"}</td><td className="py-3 pr-3"><Pill value={r.status} /></td>
          <td className="whitespace-nowrap py-3 pr-3 text-xs text-muted2">{fmtDate(r.created_at)}</td>
          <td className="py-3"><div className="flex">
            {r.status !== "Done" && <IconBtn label="Mark done" className="hover:text-success" onClick={() => act(() => adminApi.put(`/admin/records/data-deletion-requests/${r.id}`, { status: "Done" }), "Marked as done")} testId={`deletion-done-${r.reference}`}><CheckCircle2 className="h-4 w-4" /></IconBtn>}
            <DeleteBtn onConfirm={() => act(() => adminApi.delete(`/admin/records/data-deletion-requests/${r.id}`), "Deleted")} testId={`deletion-delete-${r.reference}`} />
          </div></td>
        </tr>
      ))}</tbody>
    </table></div>
  ) : <p className="py-10 text-center text-sm text-muted2">No data deletion requests.</p>;
};

const GrievanceTable = () => {
  const { data, isLoading } = useAdmin("/admin/records/grievances?limit=200");
  const refresh = useRefresh();
  const [sel, setSel] = useState(null);
  if (isLoading) return <Skel />;
  const items = data?.items || [];
  return (
    <>
      {items.length ? (
        <div className="overflow-x-auto"><table className="w-full min-w-[700px] text-sm" data-testid="grievance-table">
          <thead><tr className="border-b border-line text-left text-xs uppercase tracking-wider text-muted2">{["Reference", "Name", "Subject", "Status", "Date", ""].map((h) => <th key={h} className="py-2 pr-3">{h}</th>)}</tr></thead>
          <tbody>{items.map((g) => (
            <tr key={g.id} className="border-b border-line/60 last:border-0" data-testid={`grievance-row-${g.reference}`}>
              <td className="py-3 pr-3 font-semibold text-navy">{g.reference}</td><td className="py-3 pr-3">{g.name}<p className="text-xs text-muted2">{g.email}</p></td>
              <td className="py-3 pr-3">{g.subject}</td><td className="py-3 pr-3"><Pill value={g.status} /></td>
              <td className="whitespace-nowrap py-3 pr-3 text-xs text-muted2">{fmtDate(g.created_at)}</td>
              <td className="py-3"><div className="flex">
                <IconBtn label="Reply" onClick={() => setSel(g)} testId={`grievance-reply-${g.reference}`}><StickyNote className="h-4 w-4" /></IconBtn>
                <DeleteBtn onConfirm={async () => { await adminApi.delete(`/admin/records/grievances/${g.id}`); toast.success("Deleted"); refresh("/admin/records/grievances"); }} testId={`grievance-delete-${g.reference}`} />
              </div></td>
            </tr>
          ))}</tbody>
        </table></div>
      ) : <p className="py-10 text-center text-sm text-muted2">No grievances filed.</p>}
      {sel && <ReplyDialog g={sel} onClose={() => setSel(null)} onSaved={() => refresh("/admin/records/grievances")} />}
    </>
  );
};

export default function Requests() {
  return (
    <>
      <PageTitle title="Requests" subtitle="Data deletion requests and grievance complaints from your legal pages." />
      <Tabs defaultValue="deletion">
        <TabsList className="mb-4 rounded-full bg-white p-1 shadow-soft">
          <TabsTrigger value="deletion" className="rounded-full data-[state=active]:bg-indigo-brand data-[state=active]:text-white" data-testid="tab-deletion">Data Deletion</TabsTrigger>
          <TabsTrigger value="grievance" className="rounded-full data-[state=active]:bg-indigo-brand data-[state=active]:text-white" data-testid="tab-grievance">Grievances</TabsTrigger>
        </TabsList>
        <TabsContent value="deletion"><Card><DeletionTable /></Card></TabsContent>
        <TabsContent value="grievance"><Card><GrievanceTable /></Card></TabsContent>
      </Tabs>
    </>
  );
}
